import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { BrandContextDoc, BrandContextDocApplyWhen, BrandContextDocType } from "../../../shared/types.ts";

type ContextDocPayload = {
  name: string;
  slug?: string;
  doc_type: BrandContextDocType;
  surface: string | null;
  apply_when: BrandContextDocApplyWhen[];
  usage_prompt?: string;
  content: string;
  enabled?: boolean;
  sort_order?: number;
};

function docsQueryKey(brandId: string | null | undefined, includeDisabled: boolean) {
  return ["brands", brandId, "context-docs", includeDisabled ? "management" : "enabled-only"];
}

function requireBrandId(brandId: string | null | undefined): string {
  if (!brandId) {
    throw new Error("Brand context is required before managing context docs.");
  }
  return brandId;
}

export function useContextDocs(
  brandId: string | null | undefined,
  opts: { includeDisabled?: boolean } = {},
) {
  const includeDisabled = opts.includeDisabled === true;
  return useQuery({
    queryKey: docsQueryKey(brandId, includeDisabled),
    queryFn: () =>
      api.get<{ data: BrandContextDoc[] }>(
        `/api/brands/${encodeURIComponent(brandId!)}/context-docs${includeDisabled ? "?include_disabled=1" : ""}`,
      ),
    enabled: !!brandId,
  });
}

export function useCreateContextDoc(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContextDocPayload) =>
      api.post<{ data: BrandContextDoc }>(
        `/api/brands/${encodeURIComponent(requireBrandId(brandId))}/context-docs`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: docsQueryKey(brandId, true) });
      qc.invalidateQueries({ queryKey: docsQueryKey(brandId, false) });
      qc.invalidateQueries({ queryKey: ["brand-readiness", brandId] });
    },
  });
}

export function useUpdateContextDoc(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContextDocPayload & { slug: string }) =>
      api.put<{ data: BrandContextDoc }>(
        `/api/brands/${encodeURIComponent(requireBrandId(brandId))}/context-docs/${encodeURIComponent(payload.slug)}`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: docsQueryKey(brandId, true) });
      qc.invalidateQueries({ queryKey: docsQueryKey(brandId, false) });
      qc.invalidateQueries({ queryKey: ["brand-readiness", brandId] });
    },
  });
}

export function useDeleteContextDoc(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      api.delete<{ data: BrandContextDoc }>(
        `/api/brands/${encodeURIComponent(requireBrandId(brandId))}/context-docs/${encodeURIComponent(slug)}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: docsQueryKey(brandId, true) });
      qc.invalidateQueries({ queryKey: docsQueryKey(brandId, false) });
      qc.invalidateQueries({ queryKey: ["brand-readiness", brandId] });
    },
  });
}
